import { useEffect } from 'react';

/**
 * The live dashboard's support chat — N79. Intercom, as live runs it
 * (`src/Chat.js` on main): the same workspace, booted once for the whole app
 * with no visitor details, so its launcher sits on every page.
 *
 * One difference from live: on a phone the shell's dock floats over the bottom
 * of the screen (18px up, 56px tall), right where Intercom puts its launcher,
 * so there the launcher is lifted clear of it.
 */
const APP_ID = process.env.REACT_APP_INTERCOM_APP_ID || 'dr4s4q6s';
const WIDGET_SRC = `https://widget.intercom.io/widget/${APP_ID}`;

/** The dock's own geometry from layout.scss — 18px + 56px — plus a gap. */
const DOCK_CLEARANCE = 90;
const MOBILE = '(max-width: 900px)';

/**
 * Whether the messenger is actually running, not just its loader. Intercom
 * only serves the domains its workspace trusts: elsewhere (localhost, a new
 * host not yet added) the loader still installs `window.Intercom` and reports
 * itself booted, but never fetches the messenger — no launcher, no visitor —
 * and `show` does nothing. A visitor id is what only a running messenger has.
 */
const chatRunning = () => {
  try {
    return typeof window.Intercom === 'function' && Boolean(window.Intercom('getVisitorId'));
  } catch {
    return false;
  }
};

/**
 * Opens the chat. Where it isn't running (blocked by the browser, or a domain
 * Intercom doesn't serve), the support inbox the Help centre lists reaches the
 * same people, so the button never does nothing.
 */
export const openChat = () => {
  if (chatRunning()) {
    window.Intercom('show');
  } else {
    window.open('mailto:info@alphacapitalgroup.uk', '_blank', 'noopener');
  }
};

/** Intercom's standard loader: queue calls until the widget script arrives. */
const install = () => {
  if (typeof window.Intercom === 'function') return;
  const queue = (...args) => queue.c(args);
  queue.q = [];
  queue.c = (args) => queue.q.push(args);
  window.Intercom = queue;

  const script = document.createElement('script');
  script.async = true;
  script.src = WIDGET_SRC;
  document.head.appendChild(script);
};

export default function IntercomChat() {
  useEffect(() => {
    if (!APP_ID) return undefined;
    const mobile = window.matchMedia?.(MOBILE);
    const settings = () => ({
      api_base: 'https://api-iam.intercom.io/',
      app_id: APP_ID,
      vertical_padding: mobile?.matches ? DOCK_CLEARANCE : 20,
    });
    // An explicit `boot`, as Intercom documents for single-page apps: loaded
    // after the page, the widget doesn't boot itself off `intercomSettings`
    // (checked: its frame loads, its launcher never does). The call queues on
    // the stub until the widget script arrives.
    window.intercomSettings = settings();
    install();
    window.Intercom('boot', settings());

    // Rotating a tablet, or resizing a window across the breakpoint, moves the
    // dock in or out — the launcher follows.
    const onChange = () => window.Intercom('update', settings());
    mobile?.addEventListener?.('change', onChange);
    return () => mobile?.removeEventListener?.('change', onChange);
  }, []);

  return null;
}
